<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button default-href="/home"></ion-back-button>
        </ion-buttons>
        <ion-title>Settings</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding settings-content">
      <ion-grid>
        <ion-row class="ion-justify-content-center">
          <ion-col size="12" size-md="8" size-lg="6">

            <div class="avatar-section ion-text-center ion-padding-vertical">
              <div class="avatar-wrapper">
                <suite-avatar
                  :src="user.avatarUrl"
                  :name="`${userData?.FirstName || ''} ${userData?.LastName || ''}`"
                  size="lg"
                  class="profile-avatar"
                />
                <ion-badge class="edit-badge" @click="triggerFilePicker">
                  <ion-icon :icon="cameraOutline"></ion-icon>
                </ion-badge>
              </div>
              <ion-button fill="clear" size="small" @click="triggerFilePicker">
                {{ t('settings.changeAvatar') }}
              </ion-button>
              <input type="file" ref="fileInput" accept="image/*" class="ion-hide" @change="onAvatarChange" />
            </div>

            <ion-list-header>
              <ion-label class="title">{{ t('settings.accountDetails') }}</ion-label>
            </ion-list-header>
            <ion-list :inset="true">
              <ion-item lines="inset">
                <ion-label>
                  <p class="label-heading">{{ t('settings.name') }}</p>
                  <h2 class="readonly-text">{{ userData?.FirstName }} {{ userData?.LastName }}</h2>
                </ion-label>
                <ion-icon :icon="lockClosedOutline" slot="end" color="medium" size="small"></ion-icon>
              </ion-item>

              <ion-item>
                <ion-label>
                  <p class="label-heading">{{ t('settings.emailAddress') }}</p>
                  <h2 class="readonly-text">{{ userData?.Email }}</h2>
                </ion-label>
                <ion-icon :icon="lockClosedOutline" slot="end" color="medium" size="small"></ion-icon>
              </ion-item>

              <ion-item>
                <ion-label>
                  <p class="label-heading">{{ t('settings.assignedRoles') }}</p>
                  <div class="role-tags ion-padding-top-xs">
                    <ion-chip v-for="role in userData?.Roles" :key="role" color="secondary" disabled>
                      {{ role }}
                    </ion-chip>
                  </div>
                </ion-label>
              </ion-item>
            </ion-list>

            <ion-list-header class="ion-margin-top">
              <ion-label class="title">{{ t('settings.preferences') }}</ion-label>
            </ion-list-header>
            <ion-list :inset="true">
              <ion-item>
                <ion-label>{{ t('settings.language') }}</ion-label>
                <ion-select v-model="preferences.defaultLanguage" interface="popover" placeholder="Select Language">
                  <ion-select-option v-for="lang in SUPPORTED_LANGS" :key="lang" :value="lang">{{ lang }}</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item class="select-default-app">
                <ion-label>{{ t('settings.defaultAppDesktop') }}</ion-label>
                <ion-select v-model="preferences.defaultAppDesktop" interface="popover" placeholder="None (Show Dashboard)">
                  <ion-select-option :value="null">None (Show Dashboard)</ion-select-option>
                  <ion-select-option v-for="service in services" :key="service.id" :value="service.id">{{ tUseCase(service.label) }}</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item class="select-default-app">
                <ion-label>{{ t('settings.defaultAppMobile') }}</ion-label>
                <ion-select v-model="preferences.defaultAppMobile" interface="popover" placeholder="None (Show Dashboard)">
                  <ion-select-option :value="null">None (Show Dashboard)</ion-select-option>
                  <ion-select-option v-for="service in services" :key="service.id" :value="service.id">{{ tUseCase(service.label) }}</ion-select-option>
                </ion-select>
              </ion-item>
            </ion-list>

            <div class="action-buttons-container ion-padding-horizontal ion-padding-top ion-margin-top">
              <suite-button color="danger" fill="outline" class="btn" @click="handleLogout">
                {{ t('common.logout') }}
              </suite-button>
              <suite-button color="primary" class="btn" @click="handleSaveChanges">
                {{ t('common.save') }}
              </suite-button>
            </div>

          </ion-col>
        </ion-row>
      </ion-grid>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonGrid, IonRow, IonCol, IonButton, IonIcon,
  IonList, IonListHeader, IonItem, IonLabel, IonSelect, IonSelectOption,
  IonButtons, IonBackButton, IonChip, IonBadge
} from '@ionic/vue';
import { cameraOutline, lockClosedOutline } from 'ionicons/icons';
import { useAuth } from "@/composables/useAuth.js";
import { StorageService } from "@/services/storageService.js";
import SuiteButton from "@/components/SuiteButton.vue";
import {useSettingsStore} from "@/stores/useSettingsStore.ts";
import {useServices} from "@/composables/useServices.ts";
import {SUPPORTED_LANGS, SupportedLang} from "@/i18n.ts";
import SuiteAvatar from "@/components/SuiteAvatar.vue";
import {useLocale} from "@/composables/useLocale.ts";
import {UserPreferences} from "@/models/settings.ts";

const settingsStore = useSettingsStore()

const { userData, logout } = useAuth()
const { services } = useServices();
const { t, tUseCase, changeLocale } = useLocale();

const fileInput = ref<HTMLInputElement | null>(null);

const user = ref({
  avatarUrl: settingsStore.userProfileImage
});

const preferences = ref<UserPreferences>({
  defaultLanguage: settingsStore.suiteSettings.defaultLanguage as typeof SUPPORTED_LANGS[number],
  defaultAppDesktop: '',
  defaultAppMobile: '',
});

onMounted(async () => {
  const currentPreferences = await StorageService.Get<UserPreferences>("USER_PREFERENCES")
  if (currentPreferences) {
    Object.assign(preferences.value, currentPreferences);
  }
})

const triggerFilePicker = () => {
  if (fileInput.value) fileInput.value.click();
};

const onAvatarChange = (event: any) => {
  const file = event.target.files[0];
  if (file) {
    user.value.avatarUrl = URL.createObjectURL(file);
    // TODO: Connect logic to save file object directly to your backend API
  }
};

const handleSaveChanges = async () => {
  try {
    const err = await settingsStore.updateServicesSettings(user.value.avatarUrl, preferences.value)
    if (err) {
      console.error("updateServicesSettings err:", err);
    } else {
      await StorageService.Set("USER_PREFERENCES", preferences.value);
      const nextLang: SupportedLang = preferences.value.defaultLanguage;
      changeLocale(nextLang)
      console.log("Preferences successfully saved:", preferences.value);
    }
  } catch (error) {
    console.error("Error saving user preferences:", error);
  }
};

const handleLogout = () => {
  logout()
};
</script>

<style scoped>
.settings-content {
  --background: #fafafa;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-wrapper {
  position: relative;
  display: inline-block;
}

.profile-avatar {
  border: 3px solid #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.edit-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.label-heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ion-color-medium);
  margin-bottom: 2px;
}

.readonly-text {
  font-weight: 500;
  color: #222222;
  margin: 0;
}

/* Explicit Flex container styling fixes full-width button stretching */
.action-buttons-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.btn {
  margin: 0;
  --width: auto; /* Forces content boundaries instead of stretching */
}

.select-default-app {
  --width: 240px;
}

/* Desktop layout rules */
@media (min-width: 768px) {
  ion-list {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
    border-radius: 12px;
  }
}
</style>